import React from 'react';
import _ from 'lodash';
import {
    TableContainer,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    IconButton,
    Tooltip, ButtonBase,
} from '@mui/material';
import PdfIcon from '@mui/icons-material/PictureAsPdf';
import { Button } from './Button';
import { Navigate } from './Navigate';
import { Message } from './Message';
import { ContentPart } from './ContentPart';
import { Switch } from './Switch';
import {
    __,
    toNumber,
    formatDate,
    request,
    formatAmount,
    getAutomatPrice, getCurrency, getManualPrice, getCheckoutItemName
} from '../functions';
import { MARKET } from '../config';
import '../assets/styles/subscription.css';

/**
 * Subscription komponenta.
 */
class Subscription extends Navigate {
    /**
     * Default props.
     *
     * @type {Object}
     */
    static defaultProps = {
        user: {},
        onBuy: () => {},
    };

    /**
     * Default state.
     *
     * @type {Object}
     */
    state = {
        loading: false,
        payments: null,
        loadingPdf: null,
        before_type: '',
        type: 'automat',
        yearly: true,
    };

    /**
     * Komponenta bola pripojena.
     */
    componentDidMount() {
        this.fetch();
    }

    /**
     * Nacitame data.
     */
    fetch() {
        request('/subscriptions').then(({ data }) => {
            this.setState({
                type: data.data.type === 'manual' ? 'manual' : 'automat',
                before_type: data.data.type !== '' ? (data.data.type === 'auto' ? 'automat' : 'manual') : '',
                payments: data.data,
            });
        });
    }

    /**
     * Stiahnutie pdf.
     *
     * @param {number} id
     */
    pdf(id) {
        // Nastavime button loading
        this.setState({ loadingPdf: id });

        request(`/subscriptions/pdf/${id}`).then(response => {
            const { status, data } = response.data;

            this.setState({ loadingPdf: null });

            if (status === 'error') {
                this.showSnackbar('error', __('Nepodarilo sa stiahnuť pdf'));
                return;
            }

            // Stiahneme subor
            window.location = data.link;
        });
    }

    buy() {
        const { onBuy } = this.props;
        const { type, yearly, payments } = this.state;

        let subscription_type = '';

        if (type === 'manual') {
            subscription_type = yearly ? 'year-manual' : 'month-manual';
        } else {
            subscription_type = yearly ? 'year' : 'month';
        }

        let market = MARKET;

        if (_.has(payments.settings.invoicing, 'country') && payments.settings.invoicing.country !== '') {
            market = payments.settings.invoicing.country;
        }

        onBuy(market, subscription_type);
    }

    /**
     * Vratime menu na zaklade trhu.
     *
     * @param {string} market
     *
     * @return {string}
     */
    getCurrency(market) {
        switch (market) {
            case 'cz':
                return 'CZK';

            case 'pl':
                return 'PLN';

            case 'hu':
                return 'HUF';

            case 'us':
                return 'USD';

            case 'ro':
                return 'RON';

            case 'bg':
                return 'BGN';

            case 'dk':
                return 'DKK';

            case 'se':
                return 'SEK';

            default:
                return 'EUR';
        }
    }

    /**
     * Rendrujeme platby.
     *
     * @return {JSX.Element}
     */
    renderPayments() {
        const { payments, loadingPdf } = this.state;

        return (
            <TableContainer className="subscription-content__data__payments__table">
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>{__('Dátum')}</TableCell>
                            <TableCell>{__('Suma')}</TableCell>
                            <TableCell>{__('Popis')}</TableCell>
                            <TableCell></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {_.map(payments.payments, (payment, key) => {
                            const name = getCheckoutItemName(payment.type);

                            return (
                                <TableRow key={key}>
                                    <TableCell>{formatDate(payment.created, 'dd.mm.yyyy hh:ii')}</TableCell>
                                    <TableCell>{formatAmount(payment.amount, this.getCurrency(payment.market))}</TableCell>
                                    <TableCell>{name}</TableCell>
                                    <TableCell>
                                        <Tooltip title={payment.status === 'waiting'
                                            ? __('V najbližšom čase bude vystavená faktúra')
                                            : (payment.status === 'expecting' ? __('Čaká na úhradu') : __('Stiahnuť PDF'))}>
                                            <IconButton
                                                onClick={payment.status === 'waiting'
                                                    ? () => {}
                                                    : () => this.pdf(payment.id)}
                                            >
                                                {loadingPdf === payment.id
                                                    ? this.renderLoading(20)
                                                    : <PdfIcon color={payment.status === 'waiting' || payment.status === 'expecting'
                                                        ? 'orange'
                                                        : 'inherit'} />}
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        );
    }

    /**
     * Rendrovanie.
     *
     * @return {JSX.Element}
     */
    render() {
        const { user } = this.props;
        const { payments, loading, yearly, type, before_type } = this.state;

        if (payments === null) {
            // Data nie su nacitane
            return <div
                className="subscription-content subscription-content-loading"
            >{this.renderLoading()}</div>;
        }

        // User ma posledne dni clenstva
        const hasLastSubscriptionsDays = _.has(user, 'subscription_days')
            && user.subscription_days !== ''
            && toNumber(user.subscription_days) <= 5;

        let market = MARKET;

        if (_.has(payments.settings.invoicing, 'country') && payments.settings.invoicing.country !== '') {
            market = payments.settings.invoicing.country;
        }

        return (
            <div className="subscription-content">
                <div className="subscription-content__data">
                    <Message
                        type={user.subscription ? (hasLastSubscriptionsDays ? 'warning' : 'success') : 'error'}
                    >{user.subscription
                        ? (hasLastSubscriptionsDays
                            ? __('Vaše členstvo skončí o {DAYS} {DAYS_TEXT}')
                                .replace('{DAYS}', user.subscription_days)
                                .replace('{DAYS_TEXT}', toNumber(user.subscription_days) === 1 ? __('deň') : __('dní'))
                            : __('Vaše členstvo je aktívne a končí {END}')
                                .replace('{END}', formatDate(user.subscription_end, 'dd.mm.yyyy')))
                        : __('Vaše členstvo je neaktívne')}</Message>
                    <ContentPart title={__('Platby')} />
                    <div className="subscription-content__data__payments">
                        {_.isEmpty(payments.payments)
                            ? <div className="subscription-content__data__payments__empty">{__('Zatiaľ nemáte žiadne platby')}</div>
                            : this.renderPayments()}
                    </div>
                    <div className="subscription-content__data__panels-title">{__('Vyberte si typ členstva')}</div>
                    {before_type !== '' && before_type !== type ? <Message type="warning" style={{ marginTop: '20px' }}>{__('Meníte typ členstva, dĺžka zostávajúce členstva vám bude alikvotne upravená k pomeru ceny členstva.')}</Message> : null}
                    <div className="subscription-content__data__panels">
                        <ButtonBase
                            onClick={() => this.setState({ type: 'manual' })}
                            className={`subscription-content__data__panels__panel ${type === 'manual' ? 'panel-active' : ''}`}
                        >
                            <div className="subscription-content__data__panels__panel__name">{__('Manual')}</div>
                            <div className="subscription-content__data__panels__panel__text">{__('Ideálny pre menšie eshopy, ktoré si chcú procesy riadiť ručne a mať plnú kontrolu nad každou akciou.')}</div>
                            {yearly && market !== 'sk' ? <div className="subscription-content__data__panels__panel__price-monthly">{formatAmount(getManualPrice(false), getCurrency())}</div> : null}
                            <div className="subscription-content__data__panels__panel__price">{formatAmount(getManualPrice(yearly), getCurrency())}</div>
                            <div className="subscription-content__data__panels__panel__text-after">{__('finálna mesačná platba')}</div>
                        </ButtonBase>
                        <ButtonBase
                            onClick={() => this.setState({ type: 'automat' })}
                            className={`subscription-content__data__panels__panel ${type === 'automat' ? 'panel-active' : ''}`}
                        >
                            <div className="subscription-content__data__panels__panel__name">{__('Automat')}</div>
                            <div className="subscription-content__data__panels__panel__text">{__('Navrhnutý pre rastúce eshopy, ktoré chcú ušetriť čas automatizáciou fakturácie, skladu, zmien stavov objednávok, odosielania e-mailov a ďalších procesov.')}</div>
                            {yearly && market !== 'sk' ? <div className="subscription-content__data__panels__panel__price-monthly">{formatAmount(getAutomatPrice(false), getCurrency())}</div> : null}
                            <div className="subscription-content__data__panels__panel__price">{formatAmount(getAutomatPrice(yearly), getCurrency())}</div>
                            <div className="subscription-content__data__panels__panel__text-after">{__('finálna mesačná platba')}</div>
                        </ButtonBase>
                    </div>
                    {market !== 'sk' && toNumber(user.user_eshop_id) > 700 && toNumber(user.id) > 930 ? <div className="subscription-content__data__switch">
                        <Switch
                            label={__('Ročná platba')}
                            checked={yearly}
                            onChange={() => this.setState({ yearly: !yearly })}
                        />
                    </div> : null}
                </div>
                <div className="subscription-content__button">
                    {payments.expend_date !== null
                        ? <Message type="info">{__('Po vygenerovaní faktúry vám predlžíme členstvo do {DATE} aby ste mali čas na úhradu faktúry').replace('{DATE}', formatDate(payments.expend_date, 'dd.mm.yyyy'))}</Message>
                        : null}
                    <Button
                        onClick={() => this.buy()}
                        color="green"
                        loading={loading}
                    >{__('Kúpiť členstvo')}</Button>
                </div>
                {this.renderSnackbar()}
            </div>
        );
    }
}

export { Subscription };
